import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"

// Drives the chat composer: file picker + drag & drop staging with removable
// chips, client-side mirrors of the server limits (file size/count via data
// values), a body-length counter, and ActiveStorage direct uploads with a
// per-chip progress fill. On submit with staged files the form is held back,
// every file is uploaded to the direct-upload endpoint, and the form is then
// re-submitted carrying only the signed blob IDs as hidden fields.
export default class extends Controller {
  static targets = ["input", "chips", "body", "overlay", "status", "error", "counter", "submit"]
  static values = {
    maxFileSize: Number,
    maxFiles: Number,
    maxBodyLength: Number,
    directUploadUrl: String,
    fileTooLargeLabel: String,
    tooManyFilesMessage: String,
    uploadFailedMessage: String,
    removeFileLabel: String
  }

  connect() {
    this.dragDepth = 0
    this.uploading = false
  }

  open() { this.inputTarget.click() }

  // Enter inserts a newline (textarea default); Shift+Enter sends.
  bodyKeydown(event) {
    if (event.key !== "Enter" || !event.shiftKey) return
    event.preventDefault()
    this.element.requestSubmit()
  }

  // ---- body counter ----

  bodyChanged() {
    if (!this.hasCounterTarget) return
    const length = this.bodyTarget.value.length
    const show = length >= this.maxBodyLengthValue * 0.9
    this.counterTarget.classList.toggle("hidden", !show)
    if (show) {
      this.counterTarget.textContent =
        `${length.toLocaleString("en")} / ${this.maxBodyLengthValue.toLocaleString("en")}`
    }
  }

  // ---- submit: validate, then direct-upload, then re-submit with signed IDs ----

  submit(event) {
    if (this.uploading) { event.preventDefault(); return }
    const files = Array.from(this.inputTarget.files)
    if (files.length === 0) return // plain text (or second pass) — let Turbo take it
    if (!this.validate()) { event.preventDefault(); return }

    event.preventDefault()
    this.uploading = true
    this.submitStarted()
    Promise.all(files.map((file, index) => this.uploadFile(file, index)))
      .then((signedIds) => {
        signedIds.forEach((id) => this.appendSignedIdField(id))
        this.inputTarget.value = "" // second pass must not re-send multipart bytes
        this.uploading = false
        this.element.requestSubmit()
      })
      .catch(() => {
        this.uploading = false
        this.clearSignedIdFields()
        this.submitEnded({ detail: { success: false } })
        this.showErrors([this.uploadFailedMessageValue])
      })
  }

  uploadFile(file, index) {
    return new Promise((resolve, reject) => {
      const delegate = {
        directUploadWillStoreFileWithXHR: (request) => {
          request.upload.addEventListener("progress", (e) => {
            if (e.lengthComputable) this.setProgress(index, e.loaded / e.total)
          })
        }
      }
      new DirectUpload(file, this.directUploadUrlValue, delegate).create((error, blob) => {
        error ? reject(error) : resolve(blob.signed_id)
      })
    })
  }

  appendSignedIdField(signedId) {
    const field = document.createElement("input")
    field.type = "hidden"
    field.name = "files[]"
    field.value = signedId
    field.dataset.signedIdField = ""
    this.element.appendChild(field)
  }

  clearSignedIdFields() {
    this.element.querySelectorAll("[data-signed-id-field]").forEach((el) => el.remove())
  }

  setProgress(index, fraction) {
    const bar = this.chipsTarget.querySelector(`[data-progress-index="${index}"]`)
    if (bar) bar.style.width = `${Math.round(fraction * 100)}%`
  }

  // ---- sending status line (also used around direct uploads) ----

  submitStarted() {
    if (this.hasStatusTarget) {
      this.statusTarget.classList.remove("hidden")
      this.statusTarget.classList.add("flex")
    }
  }

  // On success the whole composer is replaced by the turbo stream, so only a
  // failed submission (e.g. rejected upload) needs the status hidden again.
  submitEnded(event) {
    if (event.detail.success) return
    if (this.hasStatusTarget) {
      this.statusTarget.classList.add("hidden")
      this.statusTarget.classList.remove("flex")
    }
  }

  // ---- drag & drop staging ----

  dragover(event) { event.preventDefault() }

  // dragenter/dragleave also fire when crossing child elements, so we track a
  // depth counter and only toggle the overlay at the outer boundary.
  dragenter(event) {
    if (!this.draggingFiles(event)) return
    event.preventDefault()
    this.dragDepth += 1
    this.showOverlay()
  }

  dragleave(event) {
    if (!this.draggingFiles(event)) return
    this.dragDepth -= 1
    if (this.dragDepth <= 0) this.hideOverlay()
  }

  drop(event) {
    event.preventDefault()
    this.dragDepth = 0
    this.hideOverlay()
    const dt = new DataTransfer()
    Array.from(this.inputTarget.files).forEach((f) => dt.items.add(f))
    Array.from(event.dataTransfer.files).forEach((f) => dt.items.add(f))
    this.inputTarget.files = dt.files
    this.renderChips()
  }

  draggingFiles(event) {
    return Array.from(event.dataTransfer?.types || []).includes("Files")
  }

  showOverlay() {
    if (!this.hasOverlayTarget) return
    this.overlayTarget.classList.remove("hidden")
    this.overlayTarget.classList.add("flex")
  }

  hideOverlay() {
    if (!this.hasOverlayTarget) return
    this.overlayTarget.classList.add("hidden")
    this.overlayTarget.classList.remove("flex")
  }

  // ---- chips + validation ----

  remove(event) {
    const removed = Number(event.currentTarget.dataset.index)
    const dt = new DataTransfer()
    Array.from(this.inputTarget.files).forEach((f, i) => { if (i !== removed) dt.items.add(f) })
    this.inputTarget.files = dt.files
    this.renderChips()
  }

  renderChips() {
    this.chipsTarget.innerHTML = ""
    Array.from(this.inputTarget.files).forEach((file, index) => {
      this.chipsTarget.appendChild(this.buildChip(file, index))
    })
    this.validate()
  }

  buildChip(file, index) {
    const oversized = file.size > this.maxFileSizeValue
    const chip = document.createElement("span")
    chip.className = "relative inline-flex items-center gap-1.5 overflow-hidden rounded-md border px-2 py-1 text-[12px] " +
      (oversized ? "border-gf-not-fit text-gf-not-fit" : "border-gf-border bg-gf-panel text-gf-ink-sub")

    const progress = document.createElement("span")
    progress.className = "absolute inset-y-0 left-0 w-0 bg-gf-accent/20 transition-[width] duration-200"
    progress.dataset.progressIndex = index
    progress.setAttribute("aria-hidden", "true")
    chip.appendChild(progress)

    const label = document.createElement("span")
    label.className = "relative"
    label.textContent = `${file.name} · ${this.formatSize(file.size)}` +
      (oversized ? ` — ${this.fileTooLargeLabelValue}` : "")
    chip.appendChild(label)

    const remove = document.createElement("button")
    remove.type = "button"
    remove.dataset.index = index
    remove.setAttribute("aria-label", this.removeFileLabelValue)
    remove.className = "relative text-gf-ink-faint hover:text-gf-not-fit"
    remove.textContent = "×"
    remove.addEventListener("click", (e) => this.remove(e))
    chip.appendChild(remove)

    return chip
  }

  validate() {
    const files = Array.from(this.inputTarget.files)
    const messages = []
    if (files.some((f) => f.size > this.maxFileSizeValue)) messages.push(this.fileTooLargeChipNote())
    if (files.length > this.maxFilesValue) messages.push(this.tooManyFilesMessageValue)
    this.showErrors(messages)
    return messages.length === 0
  }

  // The oversized chip already names the file; the error line repeats the
  // actionable half ("remove it") without duplicating filenames.
  fileTooLargeChipNote() {
    return `${this.fileTooLargeLabelValue.charAt(0).toUpperCase()}${this.fileTooLargeLabelValue.slice(1)}.`
  }

  showErrors(messages) {
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = messages.join(" ")
      this.errorTarget.classList.toggle("hidden", messages.length === 0)
    }
    if (this.hasSubmitTarget) this.submitTarget.disabled = messages.length > 0
  }

  formatSize(bytes) {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }
}
